/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, type MouseEvent, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MdmMonitoring.module.scss';

const FEED = [
  {
    severity: 'high',
    company: 'ACME LOGISTICS SP. Z O.O.',
    change: 'Status changed to In liquidation · KRS',
    time: '06:11Z',
  },
  {
    severity: 'warn',
    company: 'ACME MANUFACTURING LIMITED',
    change: 'Registered office changed · AD01 filed',
    time: '06:12Z',
  },
  { severity: 'warn', company: 'ACME NORDIC AB', change: 'Legal name changed from Acme Sverige AB', time: '06:14Z' },
  {
    severity: 'info',
    company: 'ACME HOLDINGS LIMITED',
    change: 'New subsidiary registered · ACME IBERIA S.L.',
    time: '06:15Z',
  },
  {
    severity: 'info',
    company: 'ACME MANUFACTURING GMBH',
    change: 'FY2025 accounts filed · turnover updated',
    time: '06:17Z',
  },
];

export const MdmMonitoring = () => {
  const { t } = useInt();
  const innerRef = useRef<HTMLDivElement>(null);
  const hasIntersected = useIntersection(innerRef, { threshold: 0.2 });

  const handleSpotlight = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <Section background="#0b1122" className={styles.root}>
      <div
        ref={innerRef}
        className={cn(styles.inner, hasIntersected && styles.innerIn)}
        onMouseMove={handleSpotlight}
      >
        <span className={styles.spotlight} aria-hidden="true" />

        <div className={styles.copy}>
          <TitleBlock
            title={t('Master data that maintains itself')}
            description={t(
              "Registries are read daily. When something material changes on a company you hold, the delta is pushed into your systems as an event — so the golden record is corrected before the next quarter's reporting, not during it.",
            )}
            theme="dark"
            align="left"
          />

          <Button type="white-outline" size="xl" href="/book-a-demo">
            {t('See it on your data')}
          </Button>
        </div>

        <div className={styles.feed}>
          <span className={cn(styles.corner, styles.cornerTl)} aria-hidden="true" />
          <span className={cn(styles.corner, styles.cornerBr)} aria-hidden="true" />

          <div className={styles.feedHead}>
            <span className={styles.feedSpark} aria-hidden="true">
              ✦
            </span>
            <span className={styles.feedTitle}>
              change_events
              <span className={styles.cursor} aria-hidden="true" />
            </span>
            <span className={styles.feedLive}>
              <span className={styles.liveDot} />
              {t('Synced daily')}
            </span>
          </div>

          {FEED.map((item, i) => (
            <div
              key={item.company}
              className={cn(styles.feedRow, hasIntersected && styles.feedRowIn)}
              style={{ '--i': i } as CSSProperties}
            >
              <span
                className={cn(
                  styles.badge,
                  styles[item.severity as keyof typeof styles],
                  item.severity === 'high' && styles.badgeAlert,
                )}
              >
                <i />
                {item.severity}
              </span>
              <div className={styles.feedBody}>
                <span className={styles.feedCompany}>{item.company}</span>
                <span className={styles.feedChange}>{item.change}</span>
              </div>

              <span className={styles.feedTime}>{item.time}</span>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
